import { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../../../../store";
import { toast } from "react-toastify";
import { Button, SharePopup } from "src/components/shared";
import { format } from "date-fns";

function ClassHeader() {
  const { courseStore, userStore } = useStore();

  const navigate = useNavigate();
  const [isAddedToFavorites, setIsAddedToFavorites] = useState<boolean>(false);
  const [isWishListClicked, setIsWishListClicked] = useState<boolean>(false);
  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);

  const session: any = courseStore?.classDetails;
  const course: any = courseStore?.courseDetails;
  const bookingRoom = session?.sessionBooking?.[0]?.bookingRoom;

  const getMeetingPlatform = () => {
    const link = session?.onlineSessionLink;

    if (!link) return null;

    const zoomPatterns = [
      /zoom\.us/
    ];

    const googleMeetPatterns = [
      /^https?:\/\/meet\.google\.com/,
      /^meet\.google\.com/,
    ];

    const teamsPatterns = [
      /^https?:\/\/teams\.microsoft\.com/,
      /^teams\.microsoft\.com/,
    ];

    if (googleMeetPatterns.some((pattern) => pattern.test(link))) {
      return {
        name: "Google Meet",
        logo: "/images/icons/socialMedia/GoogleMeet.svg",
      };
    } else if (teamsPatterns.some((pattern) => pattern.test(link))) {
      return {
        name: "Microsoft Teams",
        logo: "/images/icons/socialMedia/Teams.svg",
      };
    } else if (zoomPatterns.some((pattern) => pattern.test(link))) {
      return { name: "Zoom", logo: "/images/icons/socialMedia/Zoom.svg" };
    }
    return null;
  };

  useEffect(() => {
    if (userStore.user) {
      const user = userStore.user;
      const userFavoritesList: any = [];
      if (user.favoriteCourse && user.favoriteCourse.length > 0) {
        userFavoritesList.push(...user.favoriteCourse);
      }
      if (user.favoriteSession && user.favoriteSession.length > 0) {
        userFavoritesList.push(...user.favoriteSession);
      }
      const userFavoritesSessionIds = userFavoritesList.map(
        (userFavorite: any) => {
          return userFavorite.sessionId
            ? userFavorite.sessionId?.toString()
            : userFavorite.courseId?.toString();
        }
      );

      if (userFavoritesSessionIds && userFavoritesSessionIds.length > 0) {
        if (userFavoritesSessionIds.includes(session?.id)) {
          setIsAddedToFavorites(true);
        }
      }
    }
  }, [userStore.user, session]);

  const hasDiscount = session?.newSessionDiscount > 0;
  const discountPrice = hasDiscount
    ? session?.price * (1 - session?.newSessionDiscount / 100)
    : session?.price;

  const addToFavorites = async () => {
    setIsWishListClicked(true);
    if (userStore.user) {
      const response = await courseStore.addSessionToFavorites(
        session?.id,
        userStore.user.id
      );

      await userStore.getUser().then(() => {
        setIsAddedToFavorites(true);
        toast.success(
          `You’ve successfully added ${session.title} to your Wishlist.`
        );
        setIsWishListClicked(false);
      });
    } else {
      setIsWishListClicked(false);
    }
  };

  const deleteFromFavorites = async () => {
    setIsWishListClicked(true);
    if (userStore.user) {
      const favoriteObject = userStore.user.favoriteSession.find(
        (favoriteSession: any) =>
          favoriteSession.sessionId?.toString() === session.id?.toString()
      );
      await courseStore.deleteSessionFromFavorites(favoriteObject.id);
      await userStore.getUser().then(() => {
        setIsAddedToFavorites(false);
        setIsWishListClicked(false);
      });
    }
  };

  const formatDate = (date: string) => {
    if (!date) return "";

    const [rawDate] = date?.split("T");
    const [year, month, day] = rawDate?.split("-");

    const pinnedDate = new Date(+year, +month - 1, +day);

    return format(pinnedDate, "MMM d").toUpperCase();
  };

  const calculateDays = (): number => {
    const start = new Date(session?.start);
    const end = new Date(session?.end);
    const differenceInTime = end.getTime() - start.getTime();
    return Math.ceil(differenceInTime / (1000 * 3600 * 24)) + 1;
  };

  return (
    <div className="pt-3 sm:pt-4">
      <div className="flex flex-col gap-3 sm:gap-6 px-5 sm:px-8">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-1.5">
            <Button
              type={
                isAddedToFavorites ? "wishlist_on" : "wishlist_off_secondary"
              }
              text={isAddedToFavorites ? "Remove" : "Add to Wishlist"}
              icon={
                isAddedToFavorites
                  ? "/images/icons/heart-white.svg"
                  : "/images/icons/heart-pink.svg"
              }
              onClick={() =>
                isAddedToFavorites ? deleteFromFavorites() : addToFavorites()
              }
              isWishListClicked={isWishListClicked}
            />
            <Button
              type="wishlist_off_secondary"
              text="Share"
              icon="/images/icons/share-pink.svg"
              onClick={() => setIsShareOpen(true)}
            />
          </div>
          <h1 className="text-body-large font-body-large sm:text-subheading sm:font-subheading pl-4 border-l-4 border-primary-paradise-pink-500 sm:w-[390px]">
            {session?.title}
          </h1>
          {course?.courseCreator && (
            <p className="body-small">
              Created by{" "}
              <span className="text-primary-paradise-pink-500">
                {course?.courseCreator}
              </span>
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <div className="flex sm:w-[calc(50%-2px)]">
            <div className="flex items-center justify-center px-5 py-3 w-[60px] bg-primary-purple-100 border border-primary-purple-200 rounded-bl-2xl rounded-tl-2xl">
              <img
                src="/images/icons/calendar-purple.svg"
                width={20}
                height={20}
              />
            </div>
            <div className="px-4 py-2.5 w-full sm:w-[196px] bg-primary-purple-50 border border-primary-purple-100 rounded-br-2xl rounded-tr-2xl">
              <h3 className="body-medium-bold">{`${formatDate(
                session?.start
              )} - ${formatDate(session?.end)}`}</h3>
              <p className="body-small">
                {calculateDays()} {calculateDays() !== 1 ? "Days" : "Day"}
              </p>
            </div>
          </div>

          <div className="flex sm:w-[calc(50%-2px)]">
            <div className="flex items-center justify-center px-5 py-3 w-[60px] bg-primary-purple-100 border border-primary-purple-200 rounded-bl-2xl rounded-tl-2xl">
              <img
                src={
                  session?.isOnline
                    ? "/images/icons/computer.svg"
                    : "/images/icons/location-purple1.svg"
                }
                width={20}
                height={20}
              />
            </div>
            <div className="px-4 py-2.5 w-full sm:w-[196px] bg-primary-purple-50 border border-primary-purple-100 rounded-br-2xl rounded-tr-2xl">
              <h3 className="body-medium-bold">
                {session?.isOnline
                  ? "Online Class"
                  : bookingRoom?.address?.name?.split(", ")[0] || ""}
              </h3>
              {session?.isOnline ? (
                <p className="body-small">{getMeetingPlatform()?.name}</p>
              ) : (
                <a
                  href={`https://www.google.com/maps?q=${bookingRoom?.location?.lat},${bookingRoom?.location?.lng}`}
                  target="_blank"
                  className="flex items-center gap-1 body-small text-primary-purple-500 underline cursor-pointer"
                >
                  View on Map
                  <img src="/images/icons/explore-purple.svg" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {isShareOpen && (
        <SharePopup
          isOpen={isShareOpen}
          closePopup={() => setIsShareOpen(false)}
        />
      )}
    </div>
  );
}

export default observer(ClassHeader);
